import React, { useEffect } from 'react';
import { Routes, Route, useLocation } from "react-router-dom";
import Navbar from './components/Navbar';
import AdminOnboarding from './components/AdminOnboarding';
import InAppNotification from './components/InAppNots';
import UpdateDialog from './components/UpdateDialog';
import AuthBootstrap from './pages/AuthBootstrap';
import Login from './pages/Login';
import Visits from './pages/Visits';
import ShareCode from './pages/ShareCode';
import NewVisit from './pages/NewVisit';
import Home from './pages/Home';
import Camera from './pages/Camera';
import Reception from './pages/Reception';
import Profile from './pages/Profile';
import Register from './pages/Register';
import Directory from './pages/Directory';
import UserAdmin from './pages/UserAdmin';
import Activity from './pages/Activity';
import ReportError from './pages/ReportError';
import AppIssuesHelp from './pages/AppIssuesHelp';
import PasswordReset from './pages/PasswordReset';
import Amenities from './pages/Amenities';
import Amenity from './pages/Amenity';
import Reservation from './pages/Reservation';
import Approvals from './pages/Approvals';
import IssueReport from './pages/IssueReport';
import Issue from './pages/Issue';
import NewReport from './pages/NewReport';
import QRCode from './pages/QRCode';
import AllReservations from './pages/AllReservations';
import ImgUpload from './pages/ImgUploadSU';
import AmenityAdmin from './pages/AmenityAdmin';
import AdminDashboard from './pages/AdminDashboard';
import AdminBannerNotifications from './pages/AdminBannerNotifications';
import ResidentHub from './pages/ResidentHub';
import Servicios from './pages/Servicios';
import AdminHub from './pages/AdminHub';
import ParkingOnboarding from './pages/ParkingOnboarding';
import ParkingShare from './pages/ParkingShare';
import ParkRequest from './pages/ParkRequest';
import ShareParksharingImage from './pages/ShareParksharingImage';
import AirbnbForm from './pages/AirbnbForm';
import Airbnb from './pages/Airbnb';
import AirbnbReservation from './pages/AirbnbReservation';
import ManualVisit from './pages/ManualVisit';
import TourStart from './pages/TourStart';
import AppTour from './components/AppTour';
import { initializeApp } from "firebase/app";
import AuthService from './services/auth';
import wsClient from './services/ws';
import appConfig from './services/app-config';
import notificationService from './services/notification-service';

const CURRENT_APP_VERSION = process.env.REACT_APP_APP_VERSION || 'development';
const VERSION_SKIP_KEY = 'mgo-skip-version';
const CACHE_PREFIX = 'migarita-online';

function App() {
  const firebaseConfig = {
    apiKey: "AIzaSyANkG9-gqHdhK5byRpIxJUKPWMHcHCBsZ4",
    authDomain: "migaritaonline.firebaseapp.com",
    projectId: "migaritaonline",
    storageBucket: "migaritaonline.appspot.com",
    messagingSenderId: "279037079450",
    appId: "1:279037079450:web:671127ed54558d9b3f1423",
    measurementId: "G-9S0D4WWFQJ"
  };
  const app = initializeApp(firebaseConfig);
  
  // Initialize notification service
  React.useEffect(() => {
    if (AuthService.shouldLimitAutomatedAgentRequests()) {
      return;
    }
    notificationService.initialize().catch(err => {
      console.error('Failed to initialize notification service:', err);
    });
  }, []);

  const routesWithoutNavbar = ['/login', '/registro', '/guestform', '/auth/bootstrap']; 

  var last_path = null;

  const location = useLocation();
  const getPath = () => {
    return location.pathname + location.search;
  }

  const navbarOptions = {
    showSeach: false,
    showOptions: false,
    showBack: false,
    pageName: "",
    options: []
  }

  const [navbarState, setNavbarState] = React.useState(navbarOptions);
  const [history, setHistory] = React.useState([]);
  const [updateInfo, setUpdateInfo] = React.useState({ open: false, latestVersion: null });
  const isMountedRef = React.useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const checkVersion = React.useCallback(async () => {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      const response = await fetch(`/version.json?ts=${Date.now()}`, { cache: 'no-store' });
      if (!response.ok) {
        throw new Error('Failed to fetch version.json');
      }
      const data = await response.json();
      const remoteVersion = data.version;

      if (!remoteVersion || remoteVersion === CURRENT_APP_VERSION) {
        if (isMountedRef.current) {
          setUpdateInfo(prev => (prev.open ? { open: false, latestVersion: null } : prev));
        }
        return;
      }

      let skippedVersion = null;
      try {
        skippedVersion = window.sessionStorage ? window.sessionStorage.getItem(VERSION_SKIP_KEY) : null;
      } catch (storageError) {
        console.warn('Unable to read sessionStorage, continuing without persisted skip', storageError);
      }

      if (skippedVersion === remoteVersion) {
        return;
      }

      // Check debounce logic before showing dialog
      if (!appConfig.shouldShowUpdateDialog(remoteVersion)) {
        return;
      }

      if (isMountedRef.current) {
        setUpdateInfo({ open: true, latestVersion: remoteVersion });
      }
    } catch (error) {
      console.error('Version check failed', error);
    }
  }, []);

  function RouteChangeHandler({ setNavbarState }) {
    if (last_path === location.pathname) {
      return null;
    }
    if (last_path !== null) {
      console.log("Route changed to:", location.pathname);
      setNavbarState(navbarOptions);
      console.log(history);
    }
    last_path = location.pathname;
    return null; // This component doesn't render anything
  }

  useEffect(() => {
    if (AuthService.shouldLimitAutomatedAgentRequests()) {
      return undefined;
    }
    // Start global WebSocket connection on app mount
    wsClient.connect();
    return () => wsClient.disconnect();
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    checkVersion();
    return undefined;
  }, [checkVersion]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleFocus = () => {
      checkVersion();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [checkVersion]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleSwUpdate = () => {
      try {
        if (window.sessionStorage) {
          window.sessionStorage.removeItem(VERSION_SKIP_KEY);
        }
      } catch (storageError) {
        console.warn('Unable to clear skipped version flag', storageError);
      }
      checkVersion();
    };

    window.addEventListener('mgo:sw-update-available', handleSwUpdate);
    return () => window.removeEventListener('mgo:sw-update-available', handleSwUpdate);
  }, [checkVersion]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
      return undefined;
    }

    const handleSwActivated = (event) => {
      if (event.data?.type === 'PUSH_RECEIVED') {
        try {
          window.localStorage.setItem('mgo:last_push_event', JSON.stringify({
            receivedAt: Date.now(),
            payload: event.data?.payload || null,
            notification: event.data?.notification || null,
          }));
        } catch {
          // ignore storage errors
        }
        return;
      }

      if (event.data?.type === 'NOTIFICATION_CLICK') {
        try {
          window.localStorage.setItem('mgo:last_notification_click', JSON.stringify({
            clickedAt: Date.now(),
            url: event.data?.url || '/',
            notification: event.data?.notification || null,
          }));
        } catch {
          // ignore storage errors
        }

        try {
          const target = new URL(event.data.url || '/', window.location.origin);
          if (target.origin === window.location.origin) {
            window.location.href = `${target.pathname}${target.search}${target.hash}`;
          }
        } catch {
          // ignore malformed URL
        }
        return;
      }

      if (event.data?.type !== 'SW_ACTIVATED') {
        return;
      }

      console.log('[App] Service worker activated, version:', event.data?.version);
      // Clear any skipped version flags when a new SW activates
      try {
        if (window.sessionStorage) {
          window.sessionStorage.removeItem(VERSION_SKIP_KEY);
        }
      } catch (storageError) {
        console.warn('Unable to clear skipped version flag', storageError);
      }
    };

    navigator.serviceWorker.addEventListener('message', handleSwActivated);
    return () => navigator.serviceWorker.removeEventListener('message', handleSwActivated);
  }, []);

  useEffect(() => {
    const handleSwipe = (event) => {
      if (event.direction === 'right') {
        if (!hasTabs || isLeftMostTab) {
          // Show sidebar
          setSidebarVisible(true);
        }
      }
    };

    window.addEventListener('swipe', handleSwipe);
    return () => window.removeEventListener('swipe', handleSwipe);
  });

  const clearAppCaches = React.useCallback(async () => {
    if (typeof window === 'undefined' || !('caches' in window)) {
      return;
    }

    try {
      const cacheNames = await window.caches.keys();
      await Promise.all(
        cacheNames
          .filter(name => name.startsWith(CACHE_PREFIX))
          .map(name => window.caches.delete(name))
      );
    } catch (error) {
      console.warn('Failed to clear app caches', error);
    }
  }, []);

  const handleUpdateLater = React.useCallback(() => {
    if (typeof window !== 'undefined' && updateInfo.latestVersion) {
      try {
        if (window.sessionStorage) {
          window.sessionStorage.setItem(VERSION_SKIP_KEY, updateInfo.latestVersion);
        }
      } catch (storageError) {
        console.warn('Unable to store skipped version flag', storageError);
      }
      // Record that the dialog was shown
      appConfig.recordUpdateDialogShown(updateInfo.latestVersion);
    }

    if (isMountedRef.current) {
      setUpdateInfo({ open: false, latestVersion: null });
    }
  }, [updateInfo.latestVersion]);

  const handleUpdateNow = React.useCallback(async () => {
    if (typeof window !== 'undefined') {
      try {
        if (window.sessionStorage) {
          window.sessionStorage.removeItem(VERSION_SKIP_KEY);
        }
      } catch (storageError) {
        console.warn('Unable to clear skipped version flag', storageError);
      }
    }

    if (isMountedRef.current) {
      setUpdateInfo({ open: false, latestVersion: null });
    }

    await clearAppCaches();

    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          // If there's a waiting worker, activate it immediately
          if (registration.waiting) {
            // The controllerchange listener in index.html will handle the reload
            registration.waiting.postMessage({ type: 'SKIP_WAITING' });
            return;
          }

          // Try to check for updates
          if (registration.active && typeof registration.active.postMessage === 'function') {
            registration.active.postMessage({ type: 'CHECK_FOR_UPDATES' });
          }

          await registration.update();

          // Check again after update
          if (registration.waiting) {
            // The controllerchange listener in index.html will handle the reload
            registration.waiting.postMessage({ type: 'SKIP_WAITING' });
            return;
          }
        }
      } catch (error) {
        console.error('Failed to trigger service worker update', error);
      }
    }

    // Fallback: only reload if we didn't find a waiting worker
    // This ensures we don't reload too early
    if (typeof window !== 'undefined') {
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  }, []);

  return (
    <>
      <UpdateDialog
        open={updateInfo.open}
        currentVersion={CURRENT_APP_VERSION}
        latestVersion={updateInfo.latestVersion}
        onUpdateLater={handleUpdateLater}
        onUpdateNow={handleUpdateNow}
      />
      <AppTour />
      <AdminOnboarding />
      <RouteChangeHandler setNavbarState={setNavbarState} />
      {!routesWithoutNavbar.includes(location.pathname) ? (
        <Navbar
          getPath={getPath}
          navbarState={navbarState}
          setNavbarState={setNavbarState}
          history={history}
          setHistory={setHistory}
        >
          <InAppNotification />
          <Routes>
            <Route index element={<Home />} />
            <Route path="/compartir" element={<ShareCode setNavbarState={setNavbarState} navbarState={navbarState} />} />
            <Route path="/visitas/nueva" element={<NewVisit setNavbarState={setNavbarState} navbarState={navbarState} />} />
            <Route path="/visitas" element={<Visits setNavbarState={setNavbarState} navbarState={navbarState} />} />
            <Route path="/qr" element={<QRCode setNavbarState={setNavbarState} navbarState={navbarState} />} />
            <Route path="/recepcion" element={<Reception setNavbarState={setNavbarState} navbarState={navbarState} />} />
            <Route path="/visitas/manual" element={<ManualVisit setNavbarState={setNavbarState} navbarState={navbarState} />} />
            <Route path="/perfil" element={<Profile setNavbarState={setNavbarState} navbarState={navbarState} />} />
            <Route path="/directorio" element={<Directory setNavbarState={setNavbarState} navbarState={navbarState} />} />
            <Route path="/useradmin" element={<UserAdmin setNavbarState={setNavbarState} navbarState={navbarState} />} />
            <Route path="/nuevoreporte" element={<NewReport setNavbarState={setNavbarState} navbarState={navbarState} />} />
            <Route path="/actividad" element={<Activity setNavbarState={setNavbarState} navbarState={navbarState} />} />
            <Route path="/pwdreset" element={<PasswordReset setNavbarState={setNavbarState} navbarState={navbarState} />} />
            <Route path="/camara" element={<Camera setNavbarState={setNavbarState} navbarState={navbarState} />} />
            <Route path="/amenidades" element={<Amenities setNavbarState={setNavbarState} navbarState={navbarState} />} />
            <Route path="/amenidad" element={<Amenity setNavbarState={setNavbarState} navbarState={navbarState} />} />
            <Route path="/reservacion" element={<Reservation setNavbarState={setNavbarState} navbarState={navbarState} />} />
            <Route path="/reservaciones" element={<AllReservations setNavbarState={setNavbarState} navbarState={navbarState} />} />
            <Route path="/aprobaciones" element={<Approvals setNavbarState={setNavbarState} navbarState={navbarState} />} />
            <Route path="/reportes" element={<IssueReport setNavbarState={setNavbarState} navbarState={navbarState} />} />
            <Route path="/reporte" element={<Issue setNavbarState={setNavbarState} navbarState={navbarState} />} />
            <Route path="/problemas-app" element={<AppIssuesHelp setNavbarState={setNavbarState} navbarState={navbarState} />} />
            <Route path="/residenthub" element={<ResidentHub setNavbarState={setNavbarState} navbarState={navbarState} />} />
            <Route path="/servicios" element={<Servicios setNavbarState={setNavbarState} navbarState={navbarState} />} />
            <Route path="/imgupload" element={<ImgUpload setNavbarState={setNavbarState} navbarState={navbarState} />} />
            <Route path="/amenityadmin" element={<AmenityAdmin setNavbarState={setNavbarState} navbarState={navbarState} />} />
            <Route path="/adminhub" element={<AdminHub setNavbarState={setNavbarState} navbarState={navbarState} />} />
            <Route path="/admin/dashboard" element={<AdminDashboard setNavbarState={setNavbarState} navbarState={navbarState} />} />
            <Route path="/admin/banner-notifications" element={<AdminBannerNotifications setNavbarState={setNavbarState} navbarState={navbarState} />} />
            <Route path="/onboardingparqueo" element={<ParkingOnboarding setNavbarState={setNavbarState} navbarState={navbarState} />} />
            <Route path="/parqueos" element={<ParkingShare setNavbarState={setNavbarState} navbarState={navbarState} />} />
            <Route path="/solicitudparqueo" element={<ParkRequest setNavbarState={setNavbarState} navbarState={navbarState} />} />
            <Route path="/imagenparqueo" element={<ShareParksharingImage setNavbarState={setNavbarState} navbarState={navbarState} />} />
            <Route path="/airbnb" element={<Airbnb setNavbarState={setNavbarState} navbarState={navbarState} />} />
            <Route path="/airbnb/reservacion/:reservationId" element={<AirbnbReservation setNavbarState={setNavbarState} navbarState={navbarState} />} />
            <Route path="/tour" element={<TourStart />} />
            <Route path="*" element={<Home />} />
          </Routes>
        </Navbar>
      ) : (
        <>
          <InAppNotification />
          <Routes>
            <Route path="/auth/bootstrap" element={<AuthBootstrap />} />
            <Route path="/login" element={<Login setNavbarState={setNavbarState} navbarState={navbarState} />} />
            <Route path="/registro" element={<Register setNavbarState={setNavbarState} navbarState={navbarState} />} />
            <Route path="/guestform" element={<AirbnbForm setNavbarState={setNavbarState} navbarState={navbarState} />} />
          </Routes>
        </>
      )}
    </>
  );
}

export default App;